// Persists GameState to localStorage. IndexedDB can replace this later if
// save data grows too large.

import type { GameState } from "../types"

const SAVE_KEY = "echoesBeforeEternity.save"

interface SaveEnvelope {
  version: 1
  savedAt: number
  state: GameState
}

/**
 * Older saves were the bare GameState. They are still readable: anything with
 * a `permanent` field is a pre-envelope save, and losing a player's progress
 * to a storage format change would be a far worse bug than the one this
 * wrapper was added to fix.
 */
function isLegacySave(parsed: unknown): parsed is GameState {
  return typeof parsed === "object" && parsed !== null && "permanent" in parsed
}

/**
 * Backfills fields added after a save was written. Phase 6 introduced the
 * spirit's scene/question records and widened the anti-player's memory, and a
 * save from before that is missing arrays the engines iterate — a player who
 * has spent twenty loops earning permanent progress should not lose it to a
 * schema addition.
 */
function migrate(state: GameState): GameState {
  const { permanent } = state
  return {
    ...state,
    permanent: {
      ...permanent,
      avatarIndex: permanent.avatarIndex ?? 0,
      spiritScenesSeen: permanent.spiritScenesSeen ?? [],
      spiritQuestionsAsked: permanent.spiritQuestionsAsked ?? [],
      armorTier: permanent.armorTier ?? 0,
      antiPlayerState: {
        ...permanent.antiPlayerState,
        knownTrainingSites: permanent.antiPlayerState?.knownTrainingSites ?? [],
        defeatCount: permanent.antiPlayerState?.defeatCount ?? 0,
        neutralized: permanent.antiPlayerState?.neutralized ?? false,
      },
    },
  }
}

export function saveGame(state: GameState): number {
  const savedAt = Date.now()
  const envelope: SaveEnvelope = { version: 1, savedAt, state }
  localStorage.setItem(SAVE_KEY, JSON.stringify(envelope))
  return savedAt
}

export function loadGame(): GameState | null {
  const raw = localStorage.getItem(SAVE_KEY)
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (isLegacySave(parsed)) return migrate(parsed)
    const state = (parsed as SaveEnvelope).state
    return state ? migrate(state) : null
  } catch {
    return null
  }
}

/** When the current save was written, for the "saved" readout. Null for legacy saves. */
export function getSaveTimestamp(): number | null {
  const raw = localStorage.getItem(SAVE_KEY)
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (isLegacySave(parsed)) return null
    return (parsed as SaveEnvelope).savedAt ?? null
  } catch {
    return null
  }
}

export function hasSavedGame(): boolean {
  return localStorage.getItem(SAVE_KEY) !== null
}

export function clearSavedGame(): void {
  localStorage.removeItem(SAVE_KEY)
}
